/**
 * Compiles the visual builder's state into a DQL string.
 *
 * Every rule here is a live-verified fact about the parser, not a guess —
 * each ACCEPTED/REJECTED below was tested against ap1 on 2026-08-21
 * (docs/inventory/search-capture.md, "DQL grammar for the builder"):
 *
 * - `where` belongs to the STREAM clause, not a pipe stage:
 *     `stream=X where A = 'v' | duration 1h`   ACCEPTED
 *     `stream=X | where A = 'v'`               REJECTED ("Unexpected '|' before WHERE Clause")
 * - String values must be single-quoted; an unquoted word is read as a FIELD
 *   ("Unknown field(s): PACKET_BLOCKED"). Numbers may be bare.
 * - Conditions join with AND / OR; ops `=` `!=` `>` `<` `LIKE` `NOT LIKE`.
 * - Aggregation: `| groupby F | select F, count(F)` (also avg/sum/min/max on a
 *   numeric field). The aggregate column returns under a synthetic name
 *   (`count_col1`) — the caller must not assume it keeps the written name.
 *
 * The output is SHOWN to the user before running (and is theirs to edit as
 * text afterwards), so it must be idiomatic DQL, not merely accepted DQL.
 */

export const DQL_OPERATORS = ['=', '!=', '>', '<', 'LIKE', 'NOT LIKE'] as const
export type DqlOperator = (typeof DQL_OPERATORS)[number]

export interface DqlFilter {
  /** Bare field name (no `$`). */
  field: string
  op: DqlOperator
  value: string
}

export const AGG_FUNCTIONS = ['count', 'avg', 'sum', 'min', 'max'] as const
export type AggFunction = (typeof AGG_FUNCTIONS)[number]

export interface DqlAggregation {
  /** Bare field name to group rows by. */
  groupBy: string
  fn: AggFunction
  /**
   * Field the function applies to. Ignored for `count`, which counts the
   * groupBy field itself — the live-verified `count(Action)` form.
   */
  of?: string
}

/**
 * The time window a query runs over. Two shapes because the engine takes two:
 *
 * - relative: `| duration 45m` inside the query text (live-verified family).
 * - absolute: NO duration stage; the window travels OUTSIDE the query as
 *   `start_time`/`end_time` invoke-body fields, `YYYY-MM-DDTHH:mm:ss` local —
 *   exactly how the legacy console sends custom ranges (its changeFormat
 *   builds that string and its job param code sets start_time/end_time IN
 *   PLACE OF duration). Verified via legacy source for other job types; for
 *   job_type dql it is flagged for one live confirmation
 *   (docs/inventory/search-capture.md).
 */
export type DqlTime =
  | { kind: 'relative'; duration: string }
  | { kind: 'absolute'; start: string; end: string }

export interface DqlBuild {
  /**
   * Stream name(s), or '*' for all streams. Several streams emit a comma list
   * (`stream=FIREWALL,WIN-AUDIT`) — the form the legacy client itself parses:
   * its stream regex captures `[\w\*\-\,\s*]+` and splits on ','. Flagged
   * for one live parser confirmation; the separator is the only open fact.
   */
  stream: string | string[]
  time: DqlTime
  filters?: DqlFilter[]
  aggregation?: DqlAggregation | null
  limit?: number
}

/**
 * Quote a value the way the parser demands: numbers bare, everything else in
 * single quotes with embedded quotes doubled (standard SQL-style escaping; a
 * value containing a quote is rare enough that if the engine disagrees the
 * parse error is synchronous and readable).
 */
export function dqlValue(raw: string): string {
  const v = raw.trim()
  if (v !== '' && !Number.isNaN(Number(v))) return v
  return `'${v.replace(/'/g, "''")}'`
}

function condition(f: DqlFilter): string {
  return `${f.field} ${f.op} ${dqlValue(f.value)}`
}

/**
 * Builder state -> DQL. Pure; the page renders the output live as the form
 * changes, so the query language stays visible instead of hidden behind the
 * form (the builder teaches DQL rather than replacing it).
 */
export function buildDql({ stream, time, filters = [], aggregation = null, limit = 100 }: DqlBuild): string {
  const active = filters.filter((f) => f.field && f.value.trim() !== '')

  const streamList = Array.isArray(stream) ? stream.join(',') : stream
  let head = `stream=${streamList}`
  if (active.length) head += ` where ${active.map(condition).join(' AND ')}`

  // An absolute window deliberately emits NO duration stage — the window is
  // carried by the invoke body, and a duration alongside it would fight it.
  const stages = [head]
  if (time.kind === 'relative') stages.push(`duration ${time.duration}`)
  if (aggregation && aggregation.groupBy) {
    const { groupBy, fn, of } = aggregation
    const arg = fn === 'count' ? groupBy : (of ?? groupBy)
    stages.push(`groupby ${groupBy}`, `select ${groupBy}, ${fn}(${arg})`)
  }
  stages.push(`limit ${limit}`)

  return stages.join(' | ')
}

/**
 * Inject one condition into an EXISTING query's stream clause — the pivot
 * path: click a result cell, get the same search narrowed by that value.
 *
 * `where` belongs to the stream clause, before the first pipe (live-verified,
 * see the module comment), so the edit is purely textual: append to the head,
 * with AND when a where clause is already there. Returns null for any head
 * this cannot safely edit (SQL, `_fetch`, `sourcename=` forms) — the caller
 * hides the pivot rather than guessing at grammar that was never verified.
 */
export function addDqlCondition(query: string, filter: DqlFilter): string | null {
  const pipe = query.indexOf('|')
  const head = (pipe === -1 ? query : query.slice(0, pipe)).trim()
  const rest = pipe === -1 ? '' : query.slice(pipe)
  if (!/^stream\s*=/i.test(head)) return null
  const cond = condition(filter)
  // A `where` inside a quoted value would false-positive here; the result is
  // still submitted, and the engine's synchronous parse error is readable.
  const grown = /\bwhere\b/i.test(head) ? `${head} AND ${cond}` : `${head} where ${cond}`
  return rest ? `${grown} ${rest}` : grown
}

/**
 * True when a quoted literal in the query contains '|' or a bare AND — the
 * two tokens the textual stage/condition splits here key on. Such a query is
 * valid DQL but unsafe for THIS module's naive splitting, so its callers
 * refuse rather than misread.
 */
function quotedLiteralBreaksSplits(query: string): boolean {
  for (const m of query.matchAll(/'(?:[^']|'')*'/g)) {
    if (/\||\bAND\b/i.test(m[0].slice(1, -1))) return true
  }
  return false
}

/**
 * The top-values query for one field, derived from a query that RAN: same
 * stream head (where-conditions included, so counts describe the filtered
 * result on screen), same duration; the aggregation replaces every other
 * stage. Null when the head is not `stream=` (SQL, _fetch) — the facet
 * sidebar simply does not offer itself there.
 *
 * An absolute window needs no carrying here: it travels OUTSIDE the text, so
 * the caller passes the same window to invoke alongside this string.
 */
export function facetDql(query: string, field: string, top = 10): string | null {
  if (quotedLiteralBreaksSplits(query)) return null
  const stages = query.split('|').map((s) => s.trim())
  if (!/^stream\s*=/i.test(stages[0] ?? '')) return null
  const duration = stages.slice(1).find((s) => /^duration\s+/i.test(s))
  return [
    stages[0],
    ...(duration ? [duration] : []),
    `groupby ${field}`,
    `select ${field}, count(${field})`,
    `limit ${top}`,
  ].join(' | ')
}

/**
 * Which field name the engine gives a timeslice bucket. `| timeslice 5m` is
 * live-ACCEPTED (docs/inventory/search-capture.md) but the capture never ran
 * it through groupby, so the bucket field's NAME is unverified. The caller
 * probes: 'timeslice' first, then 'cnamtime' (timeslice rewriting the event
 * time to its bucket start) — each rejection is synchronous and readable, and
 * the accepted form is remembered.
 */
export type TimesliceForm = 'timeslice' | 'cnamtime'

/**
 * The per-time-bucket count query for a query that RAN: same filtered stream
 * head and duration, then timeslice + the count aggregation. Same head rules
 * as facetDql; null where the head cannot be owned.
 */
export function timelineDql(
  query: string,
  slice: string,
  form: TimesliceForm,
  /**
   * The event-time field AS THIS RESULT SPELLS IT (field names are opaque
   * and per-stream cased — 'cnamtime' on SIGNALS, 'CNAMTime' on FIREWALL).
   * Used only by the 'cnamtime' form; callers pass fieldNamed(fields,
   * 'CNAMTime') from the result they are drawing the timeline for.
   */
  timeField = 'CNAMTime',
): string | null {
  if (quotedLiteralBreaksSplits(query)) return null
  const stages = query.split('|').map((s) => s.trim())
  if (!/^stream\s*=/i.test(stages[0] ?? '')) return null
  const duration = stages.slice(1).find((s) => /^duration\s+/i.test(s))
  const f = form === 'timeslice' ? 'timeslice' : timeField
  return [
    stages[0],
    ...(duration ? [duration] : []),
    `timeslice ${slice}`,
    `groupby ${f}`,
    `select ${f}, count(${f})`,
    // Enough for any sane bucket count; the caller sizes slices for ~60.
    'limit 2000',
  ].join(' | ')
}

/**
 * The split timeline's query: per-bucket counts BROKEN OUT by one more field
 * (`groupby <bucket>, <split>`). The two-column groupby is grammar the
 * capture never exercised — callers treat a rejection exactly like the
 * timeslice probe: synchronous, readable, remembered.
 */
export function timelineSplitDql(
  query: string,
  slice: string,
  form: TimesliceForm,
  timeField: string,
  splitField: string,
): string | null {
  if (quotedLiteralBreaksSplits(query)) return null
  const stages = query.split('|').map((s) => s.trim())
  if (!/^stream\s*=/i.test(stages[0] ?? '')) return null
  const duration = stages.slice(1).find((s) => /^duration\s+/i.test(s))
  const f = form === 'timeslice' ? 'timeslice' : timeField
  return [
    stages[0],
    ...(duration ? [duration] : []),
    `timeslice ${slice}`,
    `groupby ${f}, ${splitField}`,
    `select ${f}, ${splitField}, count(${splitField})`,
    'limit 2000',
  ].join(' | ')
}

/**
 * The query with its duration stage removed — for re-running over an
 * absolute window (which REPLACES duration; both together fight). Null when
 * the text cannot be safely split.
 */
export function stripDuration(query: string): string | null {
  if (quotedLiteralBreaksSplits(query)) return null
  return query
    .split('|')
    .map((s) => s.trim())
    .filter((s) => !/^duration\s+/i.test(s))
    .join(' | ')
}

/** `'45m'` -> milliseconds; null for anything that is not `<n><s|m|h|d>`. */
export function durationToMs(duration: string): number | null {
  const m = /^(\d+)([smhd])$/i.exec(duration.trim())
  if (!m) return null
  const n = Number(m[1])
  const unit = { s: 1000, m: 60_000, h: 3_600_000, d: 86_400_000 }[m[2]!.toLowerCase()]!
  return n * unit
}

/** The duration stage's value in a query, if any (`'1h'`). */
export function queryDuration(query: string): string | null {
  if (quotedLiteralBreaksSplits(query)) return null
  for (const s of query.split('|').map((x) => x.trim())) {
    const m = /^duration\s+(\S+)$/i.exec(s)
    if (m) return m[1]!
  }
  return null
}

/** What parseDql recovered from a builder-shaped query. */
export interface ParsedDql {
  streams: string[]
  filters: DqlFilter[]
  aggregation: DqlAggregation | null
  limit: number
  /** The `duration` stage's value, or null when the query carried none (an
   * absolute-window query — the window itself lives outside the text). */
  duration: string | null
}

/**
 * The STRICT inverse of buildDql: query text -> builder state, or null.
 *
 * This is deliberately not a DQL parser. It recovers exactly the shapes
 * buildDql emits — `stream=` head, AND-joined conditions, the paired
 * groupby/select stage, duration and limit — and returns null for everything
 * else (OR, `_fetch`, SQL, stages the builder has no controls for). Null
 * means "open the builder empty", never "guess": a form seeded with a wrong
 * reading of the query would silently run something other than what the text
 * said, which is worse than no seeding at all.
 */
export function parseDql(query: string): ParsedDql | null {
  const q = query.trim()
  if (quotedLiteralBreaksSplits(q)) return null

  const stages = q.split('|').map((s) => s.trim())
  const head = /^stream\s*=\s*([\w*\s,-]+?)(?:\s+where\s+(.+))?$/i.exec(stages[0] ?? '')
  if (!head) return null
  const streams = head[1]!.split(',').map((s) => s.trim()).filter(Boolean)
  if (!streams.length) return null

  const filters: DqlFilter[] = []
  if (head[2]) {
    for (const cond of head[2].split(/\s+AND\s+/i)) {
      const m = /^([\w-]+)\s*(!=|=|>|<|NOT\s+LIKE|LIKE)\s*(.+)$/i.exec(cond.trim())
      if (!m) return null
      const op = m[2]!.toUpperCase().replace(/\s+/, ' ') as DqlOperator
      if (!DQL_OPERATORS.includes(op)) return null
      const raw = m[3]!.trim()
      // A quoted value must be ONE whole literal — `'x' OR B = 'y'` starts
      // and ends with a quote but is not one, and must fall through to null.
      const quoted = /^'(?:[^']|'')*'$/.test(raw)
      const value = quoted ? raw.slice(1, -1).replace(/''/g, "'") : raw
      // An unquoted value must be the bare number dqlValue would emit;
      // anything else is grammar this inverse does not own.
      if (!quoted && Number.isNaN(Number(raw))) return null
      filters.push({ field: m[1]!, op, value })
    }
  }

  let duration: string | null = null
  let limit = 100
  let groupBy: string | null = null
  let aggregation: DqlAggregation | null = null

  for (const stage of stages.slice(1)) {
    let m: RegExpExecArray | null
    if ((m = /^duration\s+(\S+)$/i.exec(stage))) {
      duration = m[1]!
    } else if ((m = /^limit\s+(\d+)$/i.exec(stage))) {
      limit = Number(m[1])
    } else if ((m = /^groupby\s+([\w-]+)$/i.exec(stage))) {
      groupBy = m[1]!
    } else if ((m = /^select\s+([\w-]+)\s*,\s*(\w+)\(([\w-]+)\)$/i.exec(stage))) {
      // Only the paired form buildDql writes: `groupby F | select F, fn(arg)`.
      const fn = m[2]!.toLowerCase() as AggFunction
      if (!groupBy || m[1] !== groupBy || !AGG_FUNCTIONS.includes(fn)) return null
      // buildDql's count always counts the groupBy field itself; a hand-written
      // count(OTHER) has semantics the form cannot carry — refuse, don't rewrite.
      if (fn === 'count' && m[3] !== groupBy) return null
      aggregation = { groupBy, fn, of: fn === 'count' ? undefined : m[3]! }
    } else {
      return null
    }
  }
  // A groupby whose select never arrived is not a builder query.
  if (groupBy && !aggregation) return null

  return { streams, filters, aggregation, limit, duration }
}

/**
 * Quick-pick durations. Six, not twelve: each is roughly 4-6x the previous,
 * so any window someone wants is within a factor of ~2 of a preset, and the
 * in-between cases ("45m", "3d") are exactly what the custom field beside
 * them is for. Values are what `duration` takes.
 */
export const DURATION_PRESETS = [
  { value: '15m', label: '15m' },
  { value: '1h', label: '1h' },
  { value: '6h', label: '6h' },
  { value: '24h', label: '24h' },
  { value: '7d', label: '7d' },
  { value: '30d', label: '30d' },
] as const

/** Units the custom-relative form offers, in `duration` spelling. */
export const DURATION_UNITS = [
  { value: 'm', label: 'minutes' },
  { value: 'h', label: 'hours' },
  { value: 'd', label: 'days' },
] as const

/** `2026-08-21T14:30` (datetime-local) -> the `YYYY-MM-DDTHH:mm:ss` the body wants. */
export function toApiTime(local: string): string {
  return local.length === 16 ? `${local}:00` : local
}

/** Human label for a time window, for the picker trigger. */
export function timeLabel(time: DqlTime): string {
  if (time.kind === 'relative') return `last ${time.duration}`
  const short = (s: string) => s.replace('T', ' ').slice(5, 16)
  return `${short(time.start)} → ${short(time.end)}`
}
